import React, { useEffect, useMemo, useState } from "react";
import {
  Building2,
  ChevronRight,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";
import { getAllBranches_Array } from "../../../api/hrm/branches/info";
import { getAllDepartments } from "../../../api/hrm/departments/departmentInfo";

/**
 * Phạm vi xem — cây theo Chi nhánh:
 * - Tick chi nhánh = toàn bộ NV thuộc chi nhánh đó được xem
 * - Mở chi nhánh → tick từng Bộ phận (không lẫn list phẳng toàn hệ thống)
 * - accessAll=true: mọi người có quyền module
 */
const DocumentAccessPicker = ({
  accessAll = true,
  branchIds = [],
  departmentIds = [],
  onChange,
  disabled = false,
}) => {
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [bRes, dRes] = await Promise.all([
          getAllBranches_Array(),
          getAllDepartments(),
        ]);
        if (cancelled) return;
        const bList = Array.isArray(bRes) ? bRes : bRes?.items || bRes?.data || [];
        const dList = Array.isArray(dRes) ? dRes : dRes?.items || dRes?.data || [];
        setBranches(
          bList
            .filter((x) => x?.id)
            .sort((a, b) =>
              String(a.name || a.branch_name || "").localeCompare(
                String(b.name || b.branch_name || ""),
                "vi"
              )
            )
        );
        setDepartments(dList.filter((x) => x?.id));
      } catch {
        if (!cancelled) {
          setBranches([]);
          setDepartments([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const deptsByBranch = useMemo(() => {
    const map = new Map();
    departments.forEach((d) => {
      const key = d.branch_id || "__none__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    });
    map.forEach((list) =>
      list.sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), "vi")
      )
    );
    return map;
  }, [departments]);

  const emit = (next) => {
    onChange?.({
      accessAll: next.accessAll,
      branchIds: next.branchIds || [],
      departmentIds: next.departmentIds || [],
    });
  };

  const handleToggleAll = (checked) => {
    if (checked) {
      emit({ accessAll: true, branchIds: [], departmentIds: [] });
      setQuery("");
      return;
    }
    // Chế độ giới hạn: bắt đầu trống — user chọn từng chi nhánh / bộ phận
    emit({ accessAll: false, branchIds: [], departmentIds: [] });
  };

  const toggleExpand = (branchId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(branchId)) next.delete(branchId);
      else next.add(branchId);
      return next;
    });
  };

  const setBranchChecked = (branchId, checked) => {
    const deptsOfBranch = (deptsByBranch.get(branchId) || []).map((d) => d.id);
    if (checked) {
      // Tick chi nhánh → ghi branch_id, bỏ các dept con (đã nằm trong phạm vi CN)
      emit({
        accessAll: false,
        branchIds: [...new Set([...branchIds, branchId])],
        departmentIds: departmentIds.filter((id) => !deptsOfBranch.includes(id)),
      });
    } else {
      emit({
        accessAll: false,
        branchIds: branchIds.filter((id) => id !== branchId),
        departmentIds,
      });
    }
  };

  const setDeptChecked = (branchId, deptId, checked) => {
    const deptsOfBranch = (deptsByBranch.get(branchId) || []).map((d) => d.id);
    const otherDepts = departmentIds.filter((id) => !deptsOfBranch.includes(id));
    const branchWasChecked = branchIds.includes(branchId);

    let selectedInBranch = branchWasChecked
      ? [...deptsOfBranch]
      : departmentIds.filter((id) => deptsOfBranch.includes(id));

    if (checked) {
      selectedInBranch = [...new Set([...selectedInBranch, deptId])];
    } else {
      selectedInBranch = selectedInBranch.filter((id) => id !== deptId);
    }

    // Chọn hết BP của CN → escalate thành tick cả chi nhánh
    if (
      deptsOfBranch.length > 0 &&
      deptsOfBranch.every((id) => selectedInBranch.includes(id))
    ) {
      emit({
        accessAll: false,
        branchIds: [...new Set([...branchIds.filter((id) => id !== branchId), branchId])],
        departmentIds: otherDepts,
      });
      return;
    }

    emit({
      accessAll: false,
      branchIds: branchIds.filter((id) => id !== branchId),
      departmentIds: [...otherDepts, ...selectedInBranch],
    });
  };

  const q = query.trim().toLowerCase();
  const visibleBranches = useMemo(() => {
    if (!q) return branches;
    return branches.filter((b) => {
      const name = String(b.name || b.branch_name || "").toLowerCase();
      if (name.includes(q)) return true;
      const depts = deptsByBranch.get(b.id) || [];
      return depts.some((d) =>
        String(d.name || d.department_name || "")
          .toLowerCase()
          .includes(q)
      );
    });
  }, [branches, deptsByBranch, q]);

  // Auto-expand branches that match search
  useEffect(() => {
    if (!q) return;
    const next = new Set();
    visibleBranches.forEach((b) => {
      const depts = deptsByBranch.get(b.id) || [];
      if (
        depts.some((d) =>
          String(d.name || "")
            .toLowerCase()
            .includes(q)
        )
      ) {
        next.add(b.id);
      }
    });
    if (next.size) setExpanded(next);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCount = branchIds.length + departmentIds.length;
  const summaryParts = [];
  if (branchIds.length) summaryParts.push(`${branchIds.length} chi nhánh`);
  if (departmentIds.length) summaryParts.push(`${departmentIds.length} bộ phận`);

  return (
    <div className="rounded-2xl border border-gray-100 bg-gradient-to-br from-red-50/40 via-white to-white p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-red-600 text-white flex items-center justify-center shrink-0 shadow-sm shadow-red-200">
          <ShieldCheck size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-gray-800">Phạm vi được xem</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Tick chi nhánh = cả chi nhánh. Mở rộng để chọn từng bộ phận.
          </p>
        </div>
      </div>

      <label
        className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 cursor-pointer transition ${
          accessAll
            ? "border-red-200 bg-red-50/80"
            : "border-gray-100 bg-white hover:border-gray-200"
        } ${disabled ? "opacity-60 pointer-events-none" : ""}`}
      >
        <input
          type="checkbox"
          className="h-4 w-4 accent-red-600"
          checked={accessAll}
          disabled={disabled || loading}
          onChange={(e) => handleToggleAll(e.target.checked)}
        />
        <span className="text-sm font-semibold text-gray-800">
          Tất cả chi nhánh / bộ phận
        </span>
      </label>

      {!accessAll && (
        <div className="space-y-2">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={disabled || loading}
              placeholder="Tìm chi nhánh hoặc bộ phận…"
              className="w-full rounded-xl border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-red-400"
            />
          </div>

          {summaryParts.length > 0 ? (
            <p className="text-[11px] font-semibold text-red-700">
              Đã chọn: {summaryParts.join(" · ")}
            </p>
          ) : (
            <p className="text-[11px] text-amber-700">
              Chưa chọn — hãy tick chi nhánh hoặc mở rộng để chọn bộ phận.
            </p>
          )}

          <div className="rounded-xl border border-gray-100 bg-white overflow-hidden max-h-56 overflow-y-auto custom-scrollbar">
            {loading && (
              <p className="text-xs text-gray-400 italic px-3 py-4">Đang tải…</p>
            )}
            {!loading && visibleBranches.length === 0 && (
              <p className="text-xs text-gray-400 italic px-3 py-4 text-center">
                Không tìm thấy.
              </p>
            )}
            {!loading &&
              visibleBranches.map((b) => {
                const branchId = b.id;
                const branchName = b.name || b.branch_name || "Chi nhánh";
                const depts = deptsByBranch.get(branchId) || [];
                const branchChecked = branchIds.includes(branchId);
                const selectedDeptCount = depts.filter((d) =>
                  departmentIds.includes(d.id)
                ).length;
                const isOpen = expanded.has(branchId) || Boolean(q);
                const mixed = !branchChecked && selectedDeptCount > 0;

                return (
                  <div
                    key={branchId}
                    className="border-b border-gray-50 last:border-b-0"
                  >
                    <div
                      className={`flex items-center gap-1 px-2 py-1.5 ${
                        branchChecked
                          ? "bg-red-50"
                          : mixed
                            ? "bg-rose-50/50"
                            : "hover:bg-gray-50"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleExpand(branchId)}
                        className="h-7 w-7 shrink-0 inline-flex items-center justify-center rounded-lg text-gray-400 hover:bg-white"
                        aria-label={isOpen ? "Thu gọn" : "Mở bộ phận"}
                      >
                        <ChevronRight
                          size={14}
                          className={`transition-transform ${isOpen ? "rotate-90" : ""}`}
                        />
                      </button>
                      <label className="flex-1 min-w-0 flex items-center gap-2 cursor-pointer py-0.5">
                        <input
                          type="checkbox"
                          className="accent-red-600"
                          checked={branchChecked}
                          ref={(el) => {
                            if (el) el.indeterminate = mixed;
                          }}
                          disabled={disabled}
                          onChange={(e) =>
                            setBranchChecked(branchId, e.target.checked)
                          }
                        />
                        <Building2
                          size={15}
                          className={`shrink-0 ${
                            branchChecked || mixed
                              ? "text-red-600"
                              : "text-amber-500"
                          }`}
                        />
                        <span className="flex-1 min-w-0 text-sm font-semibold text-gray-800 truncate">
                          {branchName}
                        </span>
                        <span className="shrink-0 text-[10px] font-bold text-gray-400 tabular-nums">
                          {branchChecked
                            ? "Cả CN"
                            : mixed
                              ? `${selectedDeptCount}/${depts.length} BP`
                              : `${depts.length} BP`}
                        </span>
                      </label>
                    </div>

                    {isOpen && (
                      <div className="pb-1.5 pl-10 pr-2 space-y-0.5">
                        {depts.length === 0 ? (
                          <p className="text-[11px] text-gray-400 italic px-2 py-1">
                            Chưa có bộ phận.
                          </p>
                        ) : (
                          depts
                            .filter((d) => {
                              if (!q) return true;
                              const bn = branchName.toLowerCase();
                              if (bn.includes(q)) return true;
                              return String(d.name || "")
                                .toLowerCase()
                                .includes(q);
                            })
                            .map((d) => {
                              const deptChecked =
                                branchChecked || departmentIds.includes(d.id);
                              return (
                                <label
                                  key={d.id}
                                  className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm cursor-pointer ${
                                    deptChecked
                                      ? "bg-red-50/80 text-red-800"
                                      : "hover:bg-gray-50 text-gray-700"
                                  } ${branchChecked ? "opacity-70" : ""}`}
                                >
                                  <input
                                    type="checkbox"
                                    className="accent-red-600"
                                    checked={deptChecked}
                                    disabled={disabled || branchChecked}
                                    title={
                                      branchChecked
                                        ? "Đã gồm trong chi nhánh — bỏ tick chi nhánh để chọn lẻ"
                                        : undefined
                                    }
                                    onChange={(e) =>
                                      setDeptChecked(
                                        branchId,
                                        d.id,
                                        e.target.checked
                                      )
                                    }
                                  />
                                  <Users
                                    size={13}
                                    className={
                                      deptChecked
                                        ? "text-red-500"
                                        : "text-gray-400"
                                    }
                                  />
                                  <span className="truncate">
                                    {d.name || d.department_name}
                                  </span>
                                  {branchChecked ? (
                                    <span className="ml-auto text-[10px] text-red-400 font-medium">
                                      qua CN
                                    </span>
                                  ) : null}
                                </label>
                              );
                            })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          {selectedCount === 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              Cần chọn ít nhất một chi nhánh hoặc bộ phận.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentAccessPicker;
